/**
 * Die getesteten Texte im Wortlaut auf /runs/:id, Abschnitt für Abschnitt
 * (seit 27.10.2026). Je Zeile die Rolle, die geprüfte Fassung und die
 * Vergleichsfassung; die anderen Wörter sind markiert, gleiche Abschnitte
 * stehen nur einmal. Im Feedback-Modus steht der eine Text mit seinen Rollen.
 * Der Fassungsname (`variant`) ist die Überschrift. Logik in
 * `fassungen-abgleich.ts`.
 *
 * Vorher (Entwurf 1c, 04.10.2026) standen beide Texte als Fließtext in zwei
 * Spalten, lange zugeklappt. Die Punkte am Kopf der Spalten und die Zeichen
 * (voll die geprüfte Fassung, hohl der Vergleich) bleiben von dort.
 */

import type { ReactNode } from 'react';
import { gleicheAb, geaenderte, rollenName, stueckeAus, type Teil, type Zeile } from './fassungen-abgleich';
import { locale, t } from './sprache';

export interface FassungenDaten {
  geprueft: string | null;
  vergleich: string | null;
  abschnitte_geprueft?: unknown;
  abschnitte_vergleich?: unknown;
  gliederung_geprueft?: string | null;
  gliederung_vergleich?: string | null;
}

/** Lange Seiten starten zugeklappt; ab so vielen Zeichen in beiden Fassungen zusammen. */
const LANG = 2400;
/** Ab so vielen gleichen Abschnitten hintereinander stehen sie zusammengefasst. */
const GLEICH_ZUSAMMEN = 3;

const AENDERUNG: Record<Zeile['aenderung'], { de: string; en: string; klasse: string }> = {
  geaendert: { de: 'geändert', en: 'changed', klasse: 'bg-marke/[0.1] text-marke-tief' },
  neu: { de: 'neu', en: 'new', klasse: 'bg-marke/[0.1] text-marke-tief' },
  gestrichen: { de: 'gestrichen', en: 'removed', klasse: 'bg-schlecht/[0.07] text-schlecht' },
  gleich: { de: 'gleich', en: 'same', klasse: 'bg-ebene text-text-hell' },
};

const zahl = (n: number) => new Intl.NumberFormat(locale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(n);

export function FassungenAnsicht({ texte, name, titelNeu, titelAlt, punkte, zusatzNeu, zusatzAlt }: {
  texte: FassungenDaten;
  /** Der Fassungsname des Runs, als Überschrift. Leer: keine Überschrift. */
  name: string;
  titelNeu: string;
  titelAlt: string;
  punkte?: { candidate: number; baseline: number } | null;
  zusatzNeu?: ReactNode;
  zusatzAlt?: ReactNode;
}) {
  const { geprueft, vergleich } = texte;
  if (!geprueft && !vergleich) return null;
  const neu = stueckeAus(texte.abschnitte_geprueft, geprueft);
  const alt = vergleich ? stueckeAus(texte.abschnitte_vergleich, vergleich) : [];
  const zeilen = vergleich ? gleicheAb(neu, alt) : null;
  const mitRollen = neu.some(s => s.rolle) || alt.some(s => s.rolle);
  const lang = (geprueft?.length ?? 0) + (vergleich?.length ?? 0) > LANG;

  const kopf = (
    <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
      {name
        ? <h2 className="m-0 font-serif text-[1.25rem] leading-snug font-semibold text-tinte [overflow-wrap:anywhere]">{name}</h2>
        : <h2 className="m-0 text-[0.9375rem] font-semibold text-tinte">{vergleich ? t('Die beiden Fassungen', 'The two versions') : t('Der Text', 'The copy')}</h2>}
      <span className="text-[0.8125rem] text-text-hell">{zeileUeber(zeilen, neu.length, mitRollen, texte)}</span>
    </div>
  );

  const inhalt = zeilen ? (
    <Vergleich zeilen={zeilen} titelNeu={titelNeu} titelAlt={titelAlt} punkte={punkte} zusatzNeu={zusatzNeu} zusatzAlt={zusatzAlt} />
  ) : (
    <>
      {zusatzNeu && <p className="mt-0 mb-2 text-[0.8125rem] text-text-hell">{zusatzNeu}</p>}
      <ol className="m-0 list-none p-0">
        {neu.map((s, i) => (
          <li key={i} className="grid gap-x-5 gap-y-1 border-t border-linie-zart py-2.5 first:border-t-0 sm:grid-cols-[9rem_minmax(0,1fr)]">
            <RollenFeld rolle={s.rolle} />
            <p className="m-0 font-serif text-[1.0625rem] leading-relaxed whitespace-pre-line text-tinte [overflow-wrap:anywhere]">{s.text}</p>
          </li>
        ))}
      </ol>
    </>
  );

  return (
    <section className="mt-5 border-t border-linie-zart pt-4" aria-label={t('Die getesteten Texte', 'The tested copy')}>
      {lang ? (
        <details className="group [&_summary::-webkit-details-marker]:hidden">
          <summary className="cursor-pointer list-none">
            <div className="flex items-start gap-2">
              <span aria-hidden className="mt-1.5 inline-block text-text-hell transition-transform duration-200 group-open:rotate-90">▸</span>
              <div className="min-w-0 flex-1">{kopf}</div>
            </div>
          </summary>
          {inhalt}
        </details>
      ) : <>{kopf}{inhalt}</>}
    </section>
  );
}

/** Die Zeile neben der Überschrift: was sich geändert hat und woher die Rollen stammen. */
function zeileUeber(zeilen: Zeile[] | null, abschnitte: number, mitRollen: boolean, texte: FassungenDaten): string {
  const teile: string[] = [];
  if (zeilen) {
    const n = geaenderte(zeilen);
    teile.push(n === 0
      ? t('Beide Fassungen sind gleich', 'Both versions are the same')
      : t(`${n} von ${zeilen.length} Abschnitten anders`, `${n} of ${zeilen.length} sections differ`));
  } else {
    teile.push(t(`${abschnitte} Abschnitte`, `${abschnitte} sections`));
  }
  const herkunft = texte.gliederung_geprueft ?? null;
  teile.push(!mitRollen
    ? t('Zeile für Zeile, Rollen sind für diesen Run nicht gespeichert', 'Line by line, roles were not stored for this run')
    : herkunft === 'abschnitte'
      ? t('Rollen wie beim Run angegeben', 'Roles as given for the run')
      : t('Rollen aus dem Text erkannt', 'Roles detected from the copy'));
  return teile.join(' · ');
}

function RollenFeld({ rolle, aenderung }: { rolle: Zeile['rolle']; aenderung?: Zeile['aenderung'] }) {
  return (
    <div className="flex flex-wrap items-center gap-2 sm:flex-col sm:items-start sm:gap-1.5">
      {rolle && <span className="font-mono text-[0.6875rem] font-bold tracking-[0.12em] text-text-hell uppercase">{rollenName(rolle)}</span>}
      {aenderung && aenderung !== 'gleich' && (
        <span className={`rounded px-1.5 text-[0.6875rem] font-semibold ${AENDERUNG[aenderung].klasse}`}>{t(AENDERUNG[aenderung].de, AENDERUNG[aenderung].en)}</span>
      )}
    </div>
  );
}

// Beide Seiten markieren, was nur bei ihnen steht. Bis 05.11.2026 war der
// Vergleich durchgestrichen; das las sich wie „falsch“ oder „gelöscht“,
// obwohl der Vergleich nur die Gegenfassung ist (Entscheidung Robert,
// Variante A im Entwurf „Bericht: Vergleich verständlicher“).
const MARKE_NEU = 'rounded-sm bg-marke/[0.16] text-tinte [box-decoration-break:clone]';
const MARKE_ALT = 'rounded-sm bg-ton-pflaume text-tinte [box-decoration-break:clone]';

function Teile({ teile, art }: { teile: Teil[]; art: 'neu' | 'alt' }) {
  return (
    <>
      {teile.map((x, i) => !x.anders
        ? <span key={i}>{x.text}</span>
        : art === 'neu'
          ? <mark key={i} className={MARKE_NEU}>{x.text}</mark>
          : <mark key={i} className={MARKE_ALT}>{x.text}</mark>)}
    </>
  );
}

function Punkt({ art }: { art: 'voll' | 'hohl' }) {
  return art === 'voll'
    ? <span aria-hidden className="size-2.5 shrink-0 rounded-full bg-marke" />
    : <span aria-hidden className="size-2.5 shrink-0 rounded-full border-2 border-text-leise bg-ton-pflaume" />;
}

function Vergleich({ zeilen, titelNeu, titelAlt, punkte, zusatzNeu, zusatzAlt }: {
  zeilen: Zeile[]; titelNeu: string; titelAlt: string;
  punkte?: { candidate: number; baseline: number } | null;
  zusatzNeu?: ReactNode; zusatzAlt?: ReactNode;
}) {
  const spalten = 'md:grid-cols-[9rem_minmax(0,1fr)_minmax(0,1fr)]';
  const neuKopf = <>{titelNeu}{punkte ? ` · ${zahl(punkte.candidate)}` : ''}</>;
  const altKopf = <>{titelAlt}{punkte ? ` · ${zahl(punkte.baseline)}` : ''}</>;
  // Gleiche Abschnitte hintereinander stehen ab GLEICH_ZUSAMMEN in einer
  // zugeklappten Zeile; sonst schiebt eine lange Seite die eine geänderte
  // Stelle aus dem Blick.
  const gruppen: Array<Zeile | Zeile[]> = [];
  for (const z of zeilen) {
    const letzte = gruppen[gruppen.length - 1];
    if (z.aenderung === 'gleich' && Array.isArray(letzte)) letzte.push(z);
    else gruppen.push(z.aenderung === 'gleich' ? [z] : z);
  }
  const zeile = (z: Zeile, key: string) => <AbgleichZeile key={key} z={z} spalten={spalten} titelNeu={titelNeu} titelAlt={titelAlt} />;
  return (
    <div>
      {/* Die Legende steht vor der Tabelle, damit man sie vor den Markierungen liest. */}
      <p className="mt-0 mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-[10px] bg-ebene px-3 py-2 text-[0.78125rem] text-text">
        <span><mark className={MARKE_NEU}>{t('markiert', 'marked')}</mark> {t('nur in der geprüften Fassung', 'only in the tested version')}</span>
        <span><mark className={MARKE_ALT}>{t('markiert', 'marked')}</mark> {t('nur im Vergleich', 'only in the baseline')}</span>
        <span>{t('Ohne Markierung: steht in beiden.', 'Unmarked: in both.')}</span>
      </p>
      <div className={`hidden gap-x-5 border-b border-linie-zart pb-2 text-[0.875rem] font-semibold text-tinte md:grid ${spalten}`}>
        <span className="text-[0.8125rem] font-normal text-text-hell">{t('Abschnitt', 'Section')}</span>
        <span className="inline-flex items-center gap-2 tabular-nums"><Punkt art="voll" />{neuKopf}</span>
        <span className="inline-flex items-center gap-2 tabular-nums"><Punkt art="hohl" />{altKopf}</span>
      </div>
      {/* Am Telefon steht der Kopf über allem, die Spalten stehen untereinander. */}
      <p className="mt-0 mb-2 flex flex-wrap gap-x-4 gap-y-1 text-[0.8125rem] font-semibold text-tinte md:hidden">
        <span className="inline-flex items-center gap-2 tabular-nums"><Punkt art="voll" />{neuKopf}</span>
        <span className="inline-flex items-center gap-2 tabular-nums"><Punkt art="hohl" />{altKopf}</span>
      </p>
      {(zusatzNeu || zusatzAlt) && (
        <div className={`grid gap-x-5 gap-y-1 py-2 text-[0.8125rem] text-text-hell ${spalten}`}>
          <span className="hidden md:block" />
          <span>{zusatzNeu}</span>
          <span>{zusatzAlt}</span>
        </div>
      )}
      <div>
        {gruppen.map((g, i) => !Array.isArray(g)
          ? zeile(g, String(i))
          : g.length < GLEICH_ZUSAMMEN
            ? g.map((z, k) => zeile(z, `${i}-${k}`))
            : (
              <details key={i} className="group/gleich border-t border-linie-zart [&_summary::-webkit-details-marker]:hidden">
                <summary className="flex cursor-pointer list-none items-center gap-2 py-2.5 text-[0.8125rem] text-text-hell hover:text-marke-tief">
                  <span aria-hidden className="inline-block transition-transform duration-200 group-open/gleich:rotate-90">▸</span>
                  {t(`${g.length} Abschnitte gleich`, `${g.length} sections the same`)}
                </summary>
                {g.map((z, k) => zeile(z, `${i}-${k}`))}
              </details>
            ))}
      </div>
    </div>
  );
}

function AbgleichZeile({ z, spalten, titelNeu, titelAlt }: { z: Zeile; spalten: string; titelNeu: string; titelAlt: string }) {
  const fassung = 'm-0 font-serif text-[1.0625rem] leading-relaxed whitespace-pre-line [overflow-wrap:anywhere] [text-wrap:pretty]';
  const leer = <span className="text-[0.875rem] text-text-hell">—</span>;
  // Am Telefon braucht jede Hälfte ihren Namen, weil die Spalten untereinander stehen.
  const etikett = (s: string) => <span className="mb-0.5 block text-[0.75rem] text-text-hell md:hidden">{s}</span>;
  if (z.aenderung === 'gleich') {
    return (
      <div className={`grid gap-x-5 gap-y-1 border-t border-linie-zart py-2.5 ${spalten}`}>
        <RollenFeld rolle={z.rolle} />
        <p className={`${fassung} text-text-leise md:col-span-2`}>{z.neu}</p>
      </div>
    );
  }
  return (
    <div className={`grid gap-x-5 gap-y-2 border-t border-linie-zart py-3 ${spalten}`}>
      <RollenFeld rolle={z.rolle} aenderung={z.aenderung} />
      <div className="min-w-0">
        {etikett(titelNeu)}
        {z.neu == null ? leer : <p className={`${fassung} text-tinte`}>{z.woerter ? <Teile teile={z.woerter.neu} art="neu" /> : z.neu}</p>}
      </div>
      <div className="min-w-0 rounded-md bg-ebene px-3 py-2 md:bg-transparent md:p-0">
        {etikett(titelAlt)}
        {z.alt == null ? leer : <p className={`${fassung} text-text-leise`}>{z.woerter ? <Teile teile={z.woerter.alt} art="alt" /> : z.alt}</p>}
      </div>
    </div>
  );
}
